import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Avatar } from '../../components/Avatar.tsx';
import type { CarteTour } from '../../lib/rounds.ts';
import { getCarteTour } from '../../lib/rounds.ts';
import { BasculeNet } from '../../components/BasculeNet.tsx';
import { rendusDuJoueur, useScoreNet } from '../../lib/scoreNet.ts';
import { Carte } from './Carte.tsx';

/* Les cartes de tout le monde, pendant qu'on joue.
 *
 * ── Pourquoi une modale et pas un bandeau ───────────────────────────────────
 *
 * Un bandeau de classement occupait cette place en permanence, sous la pile
 * d'avatars qui dit déjà qui mène. Deux objets pour une seule question, dont
 * un qui prenait une ligne entière de l'écran le plus disputé de
 * l'application — celui qu'on tient d'une main, ganté, en plein soleil.
 *
 * La pile reste, parce qu'un coup d'œil suffit à savoir si l'on est devant.
 * Le détail — combien de trous, quel écart, où l'autre a perdu ses coups —
 * n'est pas une chose qu'on lit en marchant : c'est une chose qu'on va
 * chercher, entre deux trous, quand la question se pose vraiment.
 *
 * ── Pourquoi la carte entière et pas seulement le total ─────────────────────
 *
 * « +2 » dit qu'on est derrière, pas pourquoi. Le ruban des trous montre où
 * l'écart s'est fait : trois pars et un triple bogey ne se jouent pas comme
 * quatre bogeys, et c'est cette lecture qui change la façon d'aborder les
 * trous restants.
 *
 * ── Du plus fort au moins fort ──────────────────────────────────────────────
 *
 * À l'écart au par, jamais au total de coups : ceux qui n'ont pas joué le même
 * nombre de trous ne sont pas comparables autrement. Qui n'a pas encore
 * marqué ferme la liste — il n'est pas dernier, il n'est pas classé.
 */
export function CartesJoueurs({ roundId, moi, onFermer }: {
    roundId: string;
    moi: string | null;
    onFermer: () => void;
}) {
    const [carte, setCarte] = useState<CarteTour | null>(null);
    const [scoreNet, setScoreNet] = useScoreNet();
    const [erreur, setErreur] = useState(false);

    useEffect(() => {
        let annule = false;
        getCarteTour(roundId)
            .then((c) => { if (!annule) setCarte(c); })
            .catch(() => { if (!annule) setErreur(true); });
        return () => { annule = true; };
    }, [roundId]);

    /* Le classement se calcule ICI et non au serveur : la carte rend les
       joueurs dans l'ordre où ils se sont assis, et cet ordre n'a aucun sens
       en cours de partie. */
    const pars = new Map((carte?.holes ?? []).map((h) => [h.number, h.par]));
    /* Les coups rendus de chaque joueur, depuis SON départ.
     *
     * Le net n'est proposé que si tout le monde peut être lu ainsi : un seul
     * joueur sans index, et le classement mêlerait des nets et des bruts. */
    const rendusDe = new Map(
        (carte?.players ?? []).map((j) => [
            j.userId,
            rendusDuJoueur(j, carte?.holes ?? [], carte?.round.maxHandicapStrokes),
        ]),
    );
    const netPossible = (carte?.players.length ?? 0) > 0 && (carte?.players ?? []).every((j) => {
        const r = rendusDe.get(j.userId);
        return r != null && r.total != null && r.indexConnus;
    });
    const enNet = scoreNet && netPossible;

    const lignes = (carte?.players ?? [])
        .map((j) => {
            const joues = j.holes.filter((h) => h.strokes != null);
            const r = rendusDe.get(j.userId);
            const ecart = joues.reduce((somme, h) => {
                const par = pars.get(h.holeNumber);
                if (par == null) return somme;
                // En net, l'écart se compte sur le score rendu : c'est tout
                // l'objet du net, et un classement mêlant les deux ne
                // classerait rien.
                const rendu = enNet ? r?.parTrou.get(h.holeNumber) ?? 0 : 0;
                return somme + (h.strokes! - rendu - par);
            }, 0);
            return { ...j, ecart: joues.length > 0 ? ecart : null };
        })
        .sort((a, b) => {
            if (a.ecart == null) return b.ecart == null ? 0 : 1;
            if (b.ecart == null) return -1;
            return a.ecart - b.ecart || b.holesPlayed - a.holesPlayed;
        });

    return (
        <div className="modal modal-open" role="dialog"
            onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
            <div className="modal-box max-w-md overflow-hidden p-0">
                <header className="flex items-start gap-3 bg-[var(--fairway-deep)] px-4 py-3.5">
                    <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-bold leading-tight text-white">
                            Les cartes
                        </h3>
                        <p className="mt-0.5 text-xs font-semibold text-white/60">
                            {carte?.round.courseName ?? 'Chargement…'}
                            {carte && ' · du plus fort au moins fort'}
                        </p>
                    </div>
                    {netPossible && (
                        <div className="shrink-0">
                            <BasculeNet net={scoreNet} onChange={setScoreNet} />
                        </div>
                    )}
                    <button type="button" onClick={onFermer} aria-label="Fermer"
                        className="-mr-1 shrink-0 rounded-field p-1.5 text-white/70 transition-colors hover:bg-white/10 hover:text-white">
                        <X size={18} />
                    </button>
                </header>

                <div className="max-h-[70dvh] space-y-2 overflow-y-auto p-2">
                    {erreur && (
                        <p className="px-2 py-6 text-center text-sm opacity-60">
                            Les cartes n’ont pas pu être chargées.
                        </p>
                    )}
                    {!erreur && carte == null && (
                        <p className="px-2 py-6 text-center text-sm opacity-60">Chargement…</p>
                    )}
                    {lignes.map((j, rang) => {
                        const cest_moi = j.userId === moi;
                        return (
                            <section key={j.userId}
                                className="rounded-box border border-base-300 bg-base-100 p-2.5 shadow-sm"
                                style={cest_moi
                                    ? { outline: '2px solid var(--color-primary)', outlineOffset: '-2px' }
                                    : undefined}>
                                <div className="flex items-center gap-2.5">
                                    {/* Le rang en chiffre, et non en médaille : le
                                        métal vit sur la pile, où il n'y a pas la
                                        place d'écrire. Ici il y a la place. */}
                                    <span className="font-score w-5 shrink-0 text-center text-lg leading-none tabular-nums opacity-70">
                                        {j.ecart != null ? rang + 1 : '—'}
                                    </span>
                                    <Avatar name={j.displayName} avatarUrl={j.avatarUrl} size={34} />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-bold">
                                            {cest_moi ? 'vous' : j.displayName}
                                        </span>
                                        <span className="block text-xs opacity-60">
                                            {j.holesPlayed}/{carte?.round.holeCount ?? '—'} trous
                                            {j.totalStrokes != null && ` · ${j.totalStrokes} coups`}
                                        </span>
                                    </span>
                                    <span className="font-score shrink-0 text-xl leading-none tabular-nums"
                                        style={{
                                            color: j.ecart == null ? undefined
                                                : j.ecart <= 0 ? 'var(--color-success)' : 'var(--color-error)',
                                        }}>
                                        {ecartTexte(j.ecart)}
                                    </span>
                                </div>

                                {/* Le même ruban que sur l'écran de jeu, en relevé :
                                    aucune case n'est un bouton, on ne navigue pas
                                    dans la carte de quelqu'un d'autre. */}
                                {carte && j.holesPlayed > 0 && (
                                    <div className="mt-2">
                                        <Carte nu holes={carte.holes} myHoles={j.holes}
                                            rendus={rendusDe.get(j.userId)} net={enNet} />
                                    </div>
                                )}
                            </section>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/** L'écart tel qu'on l'annonce au golf : rien à l'égalité, un signe sinon. */
function ecartTexte(e: number | null): string {
    if (e == null) return '—';
    if (e === 0) return 'par';
    return e > 0 ? `+${e}` : `−${Math.abs(e)}`;
}
